/**
 * Welcome popup "5% na primeira encomenda" (sections/nutry-welcome-popup.liquid).
 *
 * - Opens once, automatically, during a visitor's first visit (first session on this browser), a few seconds after
 *   they answered the cookie banner. Logged-in customers never get the markup.
 * - Visitors who closed it, or come back without signing up, get the side button instead, until they dismiss it
 *   with its X (remembered on this browser).
 * - The interest tap submits the native customer form: Shopify's hCaptcha only accepts real submits, so the page
 *   reloads and the popup resumes on the code step (?customer_posted=true). Right before that submit the dialog
 *   turns non-modal, so a visual hCaptcha challenge (appended to <body>) stays visible and usable.
 * - The popup keeps only the first name and the interest (sessionStorage) across that reload. Shopify's own
 *   captcha script may store the whole form there too; the theme doesn't control that.
 */
if (!customElements.get('nutry-welcome-popup')) {
  const KEYS = {
    firstSeen: 'nwp_first_seen', // localStorage: first page view on this browser
    shown: 'nwp_shown', // localStorage: the popup already opened by itself once
    subscribed: 'nwp_subscribed', // localStorage: signed up through the popup
    sideDismissed: 'nwp_side_dismissed', // localStorage: closed the side button with its X
    firstSession: 'nwp_first_session', // sessionStorage: this visit is the first one
    pending: 'nwp_pending', // sessionStorage: { name, interest, url } while the form posts
  };

  /**
   * Storage that never throws (private mode, blocked site data).
   * @param {'localStorage'|'sessionStorage'} type
   */
  const safeStorage = (type) => ({
    get(key) {
      try {
        return window[type].getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window[type].setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    },
    remove(key) {
      try {
        window[type].removeItem(key);
      } catch (error) {
        // Storage unavailable, nothing to remove.
      }
    },
  });

  const local = safeStorage('localStorage');
  const session = safeStorage('sessionStorage');

  /**
   * Measurement without personal data: a DOM event, a Clarity event and a dataLayer push when present.
   * @param {string} name
   * @param {object} [detail]
   */
  const track = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`nwp:${name}`, { detail }));
    if (typeof window.clarity === 'function') window.clarity('event', `popup5_${name}`);
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `popup5_${name}`, ...detail });
  };

  /** Last time the visitor answered the cookie banner on this page (ms), so the popup never opens right after it. */
  let consentAnsweredAt = 0;
  document.addEventListener('visitorConsentCollected', () => {
    consentAnsweredAt = Date.now();
  });

  /** @returns {boolean} Whether a drawer, the mobile menu or a text field is in use, so auto-open should wait. */
  const isVisitorBusy = () => {
    if (document.documentElement.classList.contains('nutry-menu-open')) return true;
    if (document.querySelector('cart-drawer[open], .js-overlay.is-visible')) return true;
    const active = document.activeElement;
    return Boolean(active && active.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]'));
  };

  /** @returns {boolean} Whether Shopify's cookie banner (or its preferences panel) is on screen. */
  const isCookieBannerVisible = () =>
    ['shopify-pc__banner', 'shopify-pc__prefs'].some((id) => {
      const element = document.getElementById(id);
      return Boolean(element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    });

  class NutryWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form) return;

      this.nameInput = this.form.querySelector('[name="contact[first_name]"]');
      this.emailInput = this.form.querySelector('[name="contact[email]"]');
      this.tagsInput = this.form.querySelector('[name="contact[tags]"]');
      this.sideButton = this.querySelector('.nwp-side');
      this.delay = Number(this.dataset.delay || 6) * 1000;
      this.step = 1;
      this.allowSubmit = false;

      this.addEventListener('click', this.#handleClick.bind(this));
      this.form.addEventListener('submit', this.#handleSubmit.bind(this));
      this.dialog.addEventListener('close', this.#handleClose.bind(this));
      // A form with two text fields and no submit button never submits on Enter, so step 1 handles Enter itself.
      [this.nameInput, this.emailInput].forEach((input) => {
        input?.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.isComposing) return;
          event.preventDefault();
          this.#next();
        });
      });
      // Back/forward cache: the page comes back as it was left, spinner included.
      this.onPageShow = (event) => {
        if (event.persisted) this.#resetSubmit();
      };
      window.addEventListener('pageshow', this.onPageShow);

      if (window.Shopify && Shopify.designMode) {
        this.#bindEditor();
        return;
      }

      if (this.#resumeAfterPost()) return;

      if (new URLSearchParams(window.location.search).get('nwp') === 'open') {
        this.open('preview');
        return;
      }
      if (local.get(KEYS.subscribed)) return;

      // Without storage every page would look like a first visit, so only the side button is offered.
      if (!local.set('nwp_test', '1')) {
        this.#showSideButton();
        return;
      }
      local.remove('nwp_test');

      if (!local.get(KEYS.firstSeen)) {
        local.set(KEYS.firstSeen, String(Date.now()));
        session.set(KEYS.firstSession, '1');
      }

      if (session.get(KEYS.firstSession) === '1' && !local.get(KEYS.shown)) {
        this.#scheduleOpen();
      } else {
        this.#showSideButton();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.openTimer);
      clearTimeout(this.submitWatchdog);
      clearInterval(this.bannerPoll);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    /**
     * @param {string} trigger - What opened it ('auto', 'side', 'preview', 'success', 'error', 'editor')
     */
    open(trigger) {
      if (this.dialog.open) return;
      if (trigger === 'auto' || trigger === 'side' || trigger === 'preview') this.#goTo(1);
      this.openedFrom = trigger;
      this.dialog.showModal();
      if (trigger !== 'editor') local.set(KEYS.shown, '1');
      if (this.sideButton) this.sideButton.hidden = true;
      this.#focusStep();
      track('open', { trigger });
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    /**
     * Waits for the cookie banner to be answered, then opens after the configured delay. Shopify injects the banner
     * after page load, so the check runs again when the timer ends: the popup never opens over the banner, never
     * sooner than the delay after it was answered, and waits while the visitor is using a drawer, the menu or a field.
     */
    #scheduleOpen() {
      const openWhenClear = () => {
        if (!isCookieBannerVisible()) {
          const sinceConsent = consentAnsweredAt ? Date.now() - consentAnsweredAt : Infinity;
          if (sinceConsent < this.delay) {
            this.openTimer = setTimeout(openWhenClear, this.delay - sinceConsent);
          } else if (isVisitorBusy()) {
            this.openTimer = setTimeout(openWhenClear, 3000);
          } else {
            this.open('auto');
          }
          return;
        }
        // The banner showed up meanwhile: open the full delay after it's answered.
        const resume = () => {
          clearInterval(this.bannerPoll);
          document.removeEventListener('visitorConsentCollected', resume);
          consentAnsweredAt = consentAnsweredAt || Date.now();
          this.openTimer = setTimeout(openWhenClear, this.delay);
        };
        document.addEventListener('visitorConsentCollected', resume);
        this.bannerPoll = setInterval(() => {
          if (!isCookieBannerVisible()) resume();
        }, 1000);
      };

      const start = () => {
        this.openTimer = setTimeout(openWhenClear, this.delay);
      };

      if (!isCookieBannerVisible()) {
        start();
        return;
      }
      const onAnswer = () => {
        clearInterval(this.bannerPoll);
        document.removeEventListener('visitorConsentCollected', onAnswer);
        start();
      };
      document.addEventListener('visitorConsentCollected', onAnswer);
      this.bannerPoll = setInterval(() => {
        if (!isCookieBannerVisible()) onAnswer();
      }, 1000);
    }

    /**
     * After the native submit, Shopify renders the page again with form errors, or redirects back on success: with
     * ?customer_posted=true, or (after its bot challenge, which drops the query) with just the form's #id in the URL.
     * Either way the popup reopens where it makes sense.
     * @returns {boolean} Whether the popup was reopened.
     */
    #resumeAfterPost() {
      let pending = null;
      try {
        pending = JSON.parse(session.get(KEYS.pending) || 'null');
      } catch (error) {
        pending = null;
      }
      if (!pending) return false;

      if (this.querySelector('[data-nwp-form-error]')) {
        session.remove(KEYS.pending);
        if (this.nameInput && pending.name) this.nameInput.value = pending.name;
        const error = this.querySelector('[data-nwp-error]');
        this.#goTo(1);
        this.open('error');
        if (error) {
          error.hidden = false;
          error.focus({ preventScroll: true });
        }
        track('error', { interest: pending.interest });
        return true;
      }

      const params = new URLSearchParams(window.location.search);
      const backFromForm = Boolean(this.form.id) && window.location.hash === `#${this.form.id}`;
      const posted =
        Boolean(this.querySelector('[data-nwp-form-success]')) || params.get('customer_posted') === 'true' || backFromForm;
      // Not back from the form yet (e.g. still on Shopify's bot challenge): keep the pending state for the next page.
      if (!posted) return false;

      session.remove(KEYS.pending);
      local.set(KEYS.subscribed, '1');
      params.delete('customer_posted');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

      const thanks = this.querySelector('[data-nwp-thanks]');
      if (thanks && pending.name) {
        thanks.textContent = thanks.dataset.template.replace('{name}', () => pending.name);
        thanks.hidden = false;
      }
      const useLink = this.querySelector('[data-nwp-use]');
      if (useLink && typeof pending.url === 'string' && pending.url.startsWith('/') && !pending.url.startsWith('//')) {
        useLink.href = pending.url;
      }

      this.#goTo(3);
      this.open('success');
      track('subscribed', { interest: pending.interest });
      return true;
    }

    /** @param {MouseEvent} event */
    #handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-nwp-close]')) {
        this.close();
      } else if (target.closest('[data-nwp-side-dismiss]')) {
        this.#dismissSideButton();
      } else if (target.closest('[data-nwp-open]')) {
        this.open('side');
      } else if (target.closest('[data-nwp-next]')) {
        this.#next();
      } else if (target.closest('[data-nwp-back]')) {
        this.#goTo(1);
      } else if (target.closest('[data-nwp-interest]')) {
        this.#chooseInterest(target.closest('[data-nwp-interest]'));
      } else if (target.closest('[data-nwp-copy]')) {
        this.#copyCode(target.closest('[data-nwp-copy]'));
      }
    }

    /**
     * Only the interest tap may really submit; any other submit attempt moves step 1 forward instead.
     * @param {SubmitEvent} event
     */
    #handleSubmit(event) {
      if (this.allowSubmit) return;
      event.preventDefault();
      if (this.step === 1) this.#next();
    }

    #handleClose() {
      // The close event is async: after a modal/non-modal swap the dialog is already open again, so skip it.
      if (this.dialog.open) return;
      if (!local.get(KEYS.subscribed)) this.#showSideButton();
      // Focus goes back to the side tab when the popup was opened from it.
      if (this.openedFrom === 'side' && this.sideButton && !this.sideButton.hidden) {
        this.sideButton.querySelector('[data-nwp-open]')?.focus({ preventScroll: true });
      }
      track('close', { step: this.step });
    }

    #next() {
      if (!this.nameInput.reportValidity() || !this.emailInput.reportValidity()) return;
      const error = this.querySelector('[data-nwp-error]');
      if (error) error.hidden = true;
      this.#goTo(2);
      track('step2');
    }

    /** @param {HTMLElement} button */
    #chooseInterest(button) {
      if (this.allowSubmit) return;
      const interest = button.dataset.nwpInterest;
      const language = this.dataset.language || 'pt';

      this.tagsInput.value = `popup_5, interesse:${interest}, idioma:${language}`;
      session.set(
        KEYS.pending,
        JSON.stringify({ name: this.nameInput.value.trim(), interest, url: button.dataset.nwpUrl })
      );

      // Come back to the exact page, query included (request.path in Liquid drops it).
      const returnInput = this.form.querySelector('[name="return_to"]');
      if (returnInput) {
        const back = new URL(window.location.href);
        back.searchParams.delete('nwp');
        back.searchParams.set('customer_posted', 'true');
        returnInput.value = `${back.pathname}${back.search}`;
      }

      this.querySelectorAll('[data-nwp-interest]').forEach((tile) => {
        tile.disabled = tile !== button;
      });
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      track('interest', { interest });

      // hCaptcha appends its visual challenge to <body>; a modal dialog would keep it hidden and inert.
      this.#setModal(false);

      // If the page hasn't left after a while (challenge abandoned or failed), let the visitor try again.
      clearTimeout(this.submitWatchdog);
      this.submitWatchdog = setTimeout(() => this.#resetSubmit(), 20000);

      this.allowSubmit = true;
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit();
      } else {
        // Safari < 16: a real submit button click fires the submit event that hCaptcha listens to.
        const submitter = document.createElement('button');
        submitter.type = 'submit';
        submitter.hidden = true;
        this.form.append(submitter);
        submitter.click();
        submitter.remove();
      }
    }

    /** Undoes the "sending" state so the interest tiles can be tapped again. */
    #resetSubmit() {
      clearTimeout(this.submitWatchdog);
      this.allowSubmit = false;
      this.querySelectorAll('[data-nwp-interest]').forEach((tile) => {
        tile.disabled = false;
        tile.classList.remove('is-loading');
        tile.removeAttribute('aria-busy');
      });
      if (this.dialog.open && this.dialog.classList.contains('nwp--submitting')) {
        this.#setModal(true);
        this.#focusStep();
      }
    }

    /**
     * Reopens the dialog as modal or non-modal; #handleClose ignores the close event this produces.
     * @param {boolean} modal
     */
    #setModal(modal) {
      if (!this.dialog.open) return;
      this.dialog.close();
      if (modal) {
        this.dialog.classList.remove('nwp--submitting');
        this.dialog.showModal();
      } else {
        this.dialog.classList.add('nwp--submitting');
        this.dialog.show();
      }
    }

    /** @param {HTMLElement} button */
    async #copyCode(button) {
      const code = this.querySelector('[data-nwp-code]');
      if (!code || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(code.textContent.trim());
        button.dataset.label ||= button.textContent;
        button.textContent = button.dataset.copied;
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          button.textContent = button.dataset.label;
        }, 2000);
        track('copy');
      } catch (error) {
        // Clipboard refused: the code stays selectable (user-select: all).
      }
    }

    /** @param {number} step */
    #goTo(step) {
      this.step = step;
      this.querySelectorAll('[data-nwp-step]').forEach((panel) => {
        panel.hidden = Number(panel.dataset.nwpStep) !== step;
      });
      this.dialog.setAttribute('aria-labelledby', `${this.dataset.sectionId}-title-${step}`);
      if (this.dialog.open) this.#focusStep();
    }

    #focusStep() {
      const title = this.querySelector(`[data-nwp-step="${this.step}"] .nwp__title`);
      if (title) title.focus({ preventScroll: true });
    }

    #showSideButton() {
      if (!this.sideButton || this.dataset.sideButton !== 'true' || local.get(KEYS.sideDismissed)) return;
      this.sideButton.hidden = false;
    }

    #dismissSideButton() {
      local.set(KEYS.sideDismissed, '1');
      if (this.sideButton) this.sideButton.hidden = true;
      track('side_dismiss');
    }

    /** In the theme editor the popup opens while its section (or a block) is selected. */
    #bindEditor() {
      const section = this.closest('.shopify-section');
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target === section) this.open('editor');
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target === section) this.close();
      });
      document.addEventListener('shopify:block:select', (event) => {
        if (!section || !section.contains(event.target)) return;
        this.#goTo(2);
        this.open('editor');
      });
    }
  }

  customElements.define('nutry-welcome-popup', NutryWelcomePopup);
}
